// Router → screen dispatch.
import { cloneElement, useEffect } from 'react';
import { useRoute } from './shell/router.js';
import { useSession } from './shell/useSession.js';
import AppShell from './shell/AppShell.jsx';
import Billing from './screens/Billing.jsx';
import { CompanyBillingProvider, BillingCreationGate } from './components/CompanyBilling.jsx';
import InitialCompanyAdmission from './components/InitialCompanyAdmission.jsx';
import { PALETTE } from './palette.js';
import { applyDocumentIdentity, storedDocumentAuthType } from './document-identity.js';
import BrandIdentity from './components/BrandIdentity.jsx';
import Beacon from './components/Beacon.jsx';
import Welcome from './screens/Welcome.jsx';
import Wizard from './screens/Wizard.jsx';
import Building from './screens/Building.jsx';
import LegacyBuildingRedirect from './screens/LegacyBuildingRedirect.jsx';
import Campaigns from './screens/Campaigns.jsx';
import OnboardingChecklist from './screens/OnboardingChecklist.jsx';
import BrandResearch from './screens/BrandResearch.jsx';
import MarketingBrief from './screens/MarketingBrief.jsx';
import Funnels from './screens/Funnels.jsx';
import Videos from './screens/Videos.jsx';
import Library from './screens/Library.jsx';
import Reports from './screens/Reports.jsx';
import Leads from './screens/Leads.jsx';
import LeadsWorkspace from './screens/LeadsWorkspace.jsx';
import Settings from './screens/Settings.jsx';
import CompanyAdmin from './screens/CompanyAdmin.jsx';
import AccountCenter from './screens/AccountCenter.jsx';
import WorkflowRecovery from './screens/WorkflowRecovery.jsx';
import PlatformOpsDashboard from './screens/PlatformOpsDashboard.jsx';
import WorkflowProgress from './screens/WorkflowProgress.jsx';
import CampaignReview from './screens/CampaignReview.jsx';
import AcceptInvite from './screens/AcceptInvite.jsx';
import MetaConnectionCallback, {
  captureAndScrubMetaCallbackInput,
} from './screens/MetaConnectionCallback.jsx';
import CuratedHome, {
  parseSelectedRenovationTypes,
} from './screens/CuratedHome.jsx';
import CuratedCampaignSetup from './screens/CuratedCampaignSetup.jsx';
import CuratedCampaigns from './screens/CuratedCampaigns.jsx';
import {
  hasNamedTenantAction,
  normalizeTenantProductPolicy,
  tenantRouteDecision,
} from './product-policy.js';

export { normalizeTenantProductPolicy };

const initialMetaCallbackInput = typeof window !== 'undefined'
  ? captureAndScrubMetaCallbackInput({
      location: window.location,
      history: window.history,
      title: document.title,
    })
  : null;

export function resolveAppRouteAccess(routeName, session) {
  const authType = session?.user?.auth?.type;
  if (authType === 'legacy') {
    const retiredGeneratedRoutes = new Set([
      'wizard',
      'building',
      'workflow',
      'campaign-review',
      'brand-research',
      'marketing-brief',
      'funnels',
      'videos',
      'library',
    ]);
    return retiredGeneratedRoutes.has(routeName)
      ? { ok: false, code: 'mvp_capability_locked' }
      : { ok: true, routeName };
  }
  if (authType !== 'supabase') return { ok: false, code: 'mvp_capability_locked' };
  if (routeName === 'accept-invite') return { ok: true, routeName };
  if (routeName === 'platform' && session?.user?.platform?.admin === true) {
    return { ok: true, routeName };
  }
  if (
    routeName === 'profile'
    && session?.user?.platform?.admin === true
    && !session?.user?.activeCompany
    && !session?.user?.tenant?.companyId
  ) {
    return { ok: true, routeName };
  }
  const policy = session?.user?.productPolicy;
  const routeDecision = tenantRouteDecision(policy, routeName);
  if (!routeDecision.ok) return routeDecision;
  if (
    routeName === 'curated-campaign'
    && !hasNamedTenantAction(policy, 'campaign.draft.create')
    && !hasNamedTenantAction(policy, 'campaign.draft.edit')
    && !hasNamedTenantAction(policy, 'company.read')
  ) {
    return { ok: false, code: 'mvp_capability_locked' };
  }
  if (routeName === 'leads' && !hasNamedTenantAction(policy, 'leads.read')) {
    return { ok: false, code: 'mvp_capability_locked' };
  }
  return routeDecision;
}

export function isTenantMetaCallbackAllowed(session) {
  const authType = session?.user?.auth?.type;
  if (authType === 'legacy') return true;
  if (authType !== 'supabase') return false;
  return tenantRouteDecision(session?.user?.productPolicy, 'company').ok === true;
}

export default function App() {
  const [route, navigate] = useRoute();
  const session = useSession();
  const authType = session.user?.auth?.type;
  const pendingAuthType = authType || storedDocumentAuthType();

  useEffect(() => {
    if (authType) applyDocumentIdentity(authType);
  }, [authType]);

  if (session.status === 'loading') {
    return <SessionLoadingScreen authType={pendingAuthType} hasCachedUser={Boolean(session.user)} />;
  }

  if (session.status === 'error') {
    return <SessionVerificationErrorScreen authType={pendingAuthType} hadCachedUser={Boolean(session.hadCachedUser)} />;
  }

  return <InitialCompanyAdmission session={session} route={route} navigate={navigate}>
    {() => <TenantApplication session={session} route={route} navigate={navigate} />}
  </InitialCompanyAdmission>;
}

function TenantApplication({ session, route, navigate }) {

  if (window.location.pathname === '/meta/callback') {
    return isTenantMetaCallbackAllowed(session)
      ? <MetaConnectionCallback session={session} callbackInput={initialMetaCallbackInput} />
      : <LockedFeatureScreen />;
  }

  let body;
  const routeAccess = resolveAppRouteAccess(route.name, session);
  if (!routeAccess.ok) {
    body = <LockedFeatureScreen />;
  } else switch (route.name) {
    case 'welcome':
    case 'home':
      body = session.user?.auth?.type === 'supabase'
        ? <CuratedHome
            session={session}
            navigate={navigate}
            canCreate={hasNamedTenantAction(session.user.productPolicy, 'campaign.draft.create')}
          />
        : <Welcome navigate={navigate} />;
      break;
    case 'wizard':
      body = <Wizard navigate={navigate} session={session} />;
      break;
    case 'curated-campaign':
      body = <CuratedCampaignEntryScreen
        session={session}
        navigate={navigate}
        campaignId={route.rest?.[0] || ''}
        selectedTypes={parseSelectedRenovationTypes(route.query?.types)}
        initialStep={route.query?.step || ''}
      />;
      break;
    case 'building':
      body = session.user?.auth?.type === 'supabase'
        ? <LegacyBuildingRedirect navigate={navigate} params={route.rest} session={session} />
        : <Building navigate={navigate} params={route.rest} />;
      break;
    case 'workflow':
      body = <WorkflowProgress navigate={navigate} params={route.rest} session={session} />;
      break;
    case 'campaign-review':
      body = <CampaignReview navigate={navigate} params={route.rest} session={session} />;
      break;
    case 'campaigns':
    case 'dashboard':                  // legacy alias — old links still work
      body = session.user?.auth?.type === 'supabase'
        ? <CuratedCampaignsEntryScreen
            session={session}
            navigate={navigate}
            campaignId={route.rest?.[0] || ''}
            canCreate={hasNamedTenantAction(session.user.productPolicy, 'campaign.draft.create')}
          />
        : <Campaigns navigate={navigate} route={route} session={session} />;
      break;
    case 'onboarding':
      body = <OnboardingChecklist navigate={navigate} session={session} />;
      break;
    case 'brand-research':
      body = <BrandResearch navigate={navigate} route={route} session={session} />;
      break;
    case 'marketing-brief':
      body = <MarketingBrief navigate={navigate} route={route} session={session} />;
      break;
    case 'funnels':
      body = <Funnels navigate={navigate} route={route} session={session} />;
      break;
    case 'videos':
      body = <Videos navigate={navigate} route={route} session={session} />;
      break;
    case 'library':
      body = <Library navigate={navigate} session={session} />;
      break;
    case 'reports':
    case 'live-campaigns':             // legacy alias
      body = <Reports navigate={navigate} session={session} />;
      break;
    case 'leads':
      body = session.user?.auth?.type === 'supabase'
        ? <CuratedLeadsEntryScreen navigate={navigate} route={route} session={session} />
        : <Leads navigate={navigate} route={route} session={session} />;
      break;
    case 'settings':
      body = <Settings session={session} navigate={navigate} />;
      break;
    case 'billing':
      body = <Billing session={session} navigate={navigate} route={route} />;
      break;
    case 'company':
      body = <CompanyAdmin key={`${session.user?.activeCompany?.id}:${session.user?.role}`} route={route} session={session} navigate={navigate} onTenantContextChange={session.setActiveTenantContext} />;
      break;
    case 'profile':
      body = <AccountCenter session={session} navigate={navigate} />;
      break;
    case 'accept-invite':
      body = <AcceptInvite route={route} navigate={navigate} session={session} />;
      break;
    case 'recovery':
      body = <WorkflowRecovery session={session} />;
      break;
    case 'usage':
      body = session.user?.platform?.admin
        ? <PlatformOpsDashboard session={session} navigate={navigate} />
        : <Reports navigate={navigate} session={session} />;
      break;
    case 'platform':
      body = <PlatformOpsDashboard
        session={session}
        navigate={navigate}
        requestedView={route.rest?.[0] || 'overview'}
      />;
      break;
    default:
      body = session.user?.auth?.type === 'legacy'
        ? <Welcome navigate={navigate} />
        : <LockedFeatureScreen />;
  }

  return (
    <CompanyBillingProvider session={session} navigate={navigate}>
      {billingNavigate => <AppShell route={route} navigate={billingNavigate} session={session}>
        {routeAccess.ok && route.name === 'curated-campaign'
          ? <BillingCreationGate navigate={billingNavigate} required={!route.rest?.[0]} selectedTypes={parseSelectedRenovationTypes(route.query?.types)} fallback={<CuratedHome session={session} navigate={billingNavigate} canCreate={hasNamedTenantAction(session.user?.productPolicy, 'campaign.draft.create')} />}>{cloneElement(body, { navigate: billingNavigate })}</BillingCreationGate>
          : cloneElement(body, { navigate: billingNavigate })}
      </AppShell>}
    </CompanyBillingProvider>
  );
}

export function LockedFeatureScreen() {
  return (
    <section style={laneScreenStyle} aria-labelledby="locked-feature-title">
      <div style={laneEyebrowStyle}>More · Coming Soon</div>
      <h1 id="locked-feature-title" style={laneTitleStyle}>Coming Soon</h1>
      <p style={laneBodyStyle}>
        This capability is preserved safely for a future release and is not available in the current MVP.
      </p>
    </section>
  );
}

export function CuratedCampaignEntryScreen(props = {}) {
  return <CuratedCampaignSetup {...props} />;
}

export function CuratedCampaignsEntryScreen(props = {}) {
  return <CuratedCampaigns {...props} />;
}

export function CuratedLeadsEntryScreen(props = {}) {
  return <LeadsWorkspace {...props} />;
}

export function SessionLoadingScreen({ authType = null, hasCachedUser = false } = {}) {
  const isActiveBrand = authType === 'supabase';
  return (
    <div className={isActiveBrand ? 'bl-brand-surface' : undefined} style={loadingScreenStyle} role="status" aria-live="polite" aria-busy="true">
      <div style={loadingPanelStyle}>
        {isActiveBrand ? <BrandIdentity detail="Curated Meta ads" /> : null}
        <Beacon size="page" delayMs={0} slowAfterMs={null} />
        <div style={loadingTitleStyle}>Loading workspace</div>
        <div style={loadingBodyStyle}>
          {hasCachedUser
            ? 'Verifying your session and refreshing the latest workspace data.'
            : 'Checking your session before opening protected workspace pages.'}
        </div>
      </div>
    </div>
  );
}

export function SessionVerificationErrorScreen({ authType = null, hadCachedUser = false } = {}) {
  const isActiveBrand = authType === 'supabase';
  return (
    <div className={isActiveBrand ? 'bl-brand-surface' : undefined} style={loadingScreenStyle} role="alert">
      <div style={loadingPanelStyle}>
        {isActiveBrand ? <BrandIdentity detail="Curated Meta ads" /> : null}
        <div style={errorBadgeStyle} aria-hidden="true">!</div>
        <div style={loadingTitleStyle}>Session could not be verified</div>
        <div style={loadingBodyStyle}>
          {hadCachedUser
            ? 'Your saved workspace session could not be refreshed. Try again, or sign in again if the problem continues.'
            : 'The workspace could not confirm your session. Try again, or sign in again if the problem continues.'}
        </div>
        <button
          type="button"
          style={retryButtonStyle}
          onClick={() => window.location.reload()}
        >
          Refresh
        </button>
      </div>
    </div>
  );
}

const laneScreenStyle = {
  width: 'min(100%, 760px)',
  margin: '0 auto',
  padding: 'clamp(40px, 8vw, 88px) 28px',
  color: PALETTE.ink,
  fontFamily: 'var(--active-font-ui, Inter, sans-serif)',
};

const laneEyebrowStyle = {
  marginBottom: 10,
  color: 'var(--active-accent-deep, #F6C744)',
  fontSize: 12,
  fontWeight: 800,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
};

const laneTitleStyle = {
  margin: 0,
  fontSize: 'clamp(30px, 5vw, 48px)',
  lineHeight: 1.05,
};

const laneBodyStyle = {
  maxWidth: 620,
  margin: '18px 0 0',
  color: PALETTE.ink2,
  fontSize: 16,
  lineHeight: 1.65,
};

const loadingScreenStyle = {
  minHeight: '100vh',
  display: 'grid',
  placeItems: 'center',
  padding: 24,
  background: PALETTE.bg,
  color: PALETTE.ink,
  fontFamily: 'var(--active-font-ui, Inter, sans-serif)',
};

const loadingPanelStyle = {
  width: 'min(100%, 420px)',
  display: 'grid',
  justifyItems: 'center',
  gap: 14,
  padding: 28,
  border: `1px solid ${PALETTE.line}`,
  borderRadius: 14,
  background: PALETTE.surface,
  boxShadow: '0 12px 30px rgba(26, 23, 20, 0.08)',
  textAlign: 'center',
};

const loadingTitleStyle = {
  fontSize: 17,
  fontWeight: 800,
  color: PALETTE.ink,
};

const loadingBodyStyle = {
  maxWidth: 320,
  color: PALETTE.ink2,
  fontSize: 13,
  lineHeight: 1.5,
};

const errorBadgeStyle = {
  width: 34,
  height: 34,
  borderRadius: '50%',
  display: 'grid',
  placeItems: 'center',
  background: PALETTE.dangerSoft,
  color: PALETTE.danger,
  fontSize: 18,
  fontWeight: 900,
};

const retryButtonStyle = {
  minHeight: 42,
  minWidth: 116,
  border: `1px solid ${PALETTE.controlBorder}`,
  borderRadius: 8,
  background: PALETTE.surface,
  color: PALETTE.ink,
  fontSize: 14,
  fontWeight: 800,
  cursor: 'pointer',
};
